import { useMemo, useState } from "react";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import { useAuth } from "../../../../auth/useAuth";
import {
  fetchTransportJobRuns,
  patchTransportJobSchedule,
  runTransportJobNow,
} from "../../../../api/transportSchedulerApi";
import DataSourceControlsGrid from "../dataSources/DataSourceControlsGrid";
import DataSourceScheduleModal from "../dataSources/DataSourceScheduleModal";
import { mapTransportSourceToGridRow } from "../dataSources/mapDataSourceGridRow";
import { useOverlaySourceSchedule } from "../dataSources/useOverlaySourceSchedule";

const TransportSourceControlsTable = ({
  sources = [],
  filters = {},
  onRefresh,
  showToast,
  onSourcePatch,
  hideEmpty = false,
}) => {
  const [busyCode, setBusyCode] = useState(null);
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManageSources =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const overlaySchedule = useOverlaySourceSchedule({
    domain: "transport",
    onRefresh,
    showToast,
    onScheduleSaved: (source, schedule) => {
      onSourcePatch?.(source.sourceCode, {
        scheduleEnabled: schedule.scheduleEnabled,
        scheduleIntervalMinutes: schedule.scheduleIntervalMinutes,
        nextRunAt: schedule.nextRunAt,
        supportsSchedule: true,
      });
    },
  });

  const [nativeScheduleSource, setNativeScheduleSource] = useState(null);
  const [nativeScheduleSaving, setNativeScheduleSaving] = useState(false);

  const filteredSources = useMemo(() => {
    let list = [...sources];

    if (filters.search) {
      const query = filters.search.toLowerCase();
      list = list.filter(
        (source) =>
          source.sourceName?.toLowerCase().includes(query) ||
          source.sourceCode?.toLowerCase().includes(query)
      );
    }

    if (filters.enabledFilter === "enabled") {
      list = list.filter((source) => source.isActive);
    } else if (filters.enabledFilter === "disabled") {
      list = list.filter((source) => !source.isActive);
    }

    if (filters.statusFilter && filters.statusFilter !== "all") {
      list = list.filter((source) => source.healthStatus === filters.statusFilter);
    }

    return list
      .sort((a, b) => (a.sourceName || "").localeCompare(b.sourceName || ""))
      .map((source) => mapTransportSourceToGridRow(source));
  }, [sources, filters]);

  const handleRunNow = async (source) => {
    if (!source.transportJobKey) {
      showToast?.(`No automated runner is mapped to ${source.displayName}.`, "error");
      return;
    }
    setBusyCode(source.sourceKey);
    try {
      await runTransportJobNow(source.transportJobKey, { triggeredBy: "admin:data-sources" });
      showToast?.(`Transport job started for ${source.displayName}.`, "success");
      onRefresh?.({ silent: true });
    } catch (error) {
      showToast?.(error.message || "Unable to start transport job.", "error");
    } finally {
      setBusyCode(null);
    }
  };

  const handleViewRuns = async (source) => {
    if (!source.transportJobKey) {
      showToast?.("Run history is available after a scheduler job is mapped to this source.", "neutral");
      return;
    }
    try {
      const payload = await fetchTransportJobRuns(source.transportJobKey, { limit: 10 });
      const count = payload.runs?.length || payload.records?.length || 0;
      showToast?.(`${source.displayName}: ${count} recent scheduler run(s).`, "neutral");
    } catch (error) {
      showToast?.(error.message || "Unable to load transport job runs.", "error");
    }
  };

  const handleSaveNativeSchedule = async (source, payload) => {
    setNativeScheduleSaving(true);
    try {
      await patchTransportJobSchedule(source.transportJobKey, {
        enabled: payload.scheduleEnabled === true,
        intervalMinutes: payload.scheduleIntervalMinutes,
      });
      onSourcePatch?.(source.sourceCode, {
        scheduleEnabled: payload.scheduleEnabled === true,
        scheduleIntervalMinutes: payload.scheduleIntervalMinutes,
        supportsSchedule: true,
      });
      setNativeScheduleSource(null);
      showToast?.(`Schedule updated for ${source.displayName}.`, "success");
      onRefresh?.({ silent: true });
    } catch (error) {
      showToast?.(error.message || "Unable to save transport schedule.", "error");
    } finally {
      setNativeScheduleSaving(false);
    }
  };

  if (!filteredSources.length) {
    if (hideEmpty) {
      return null;
    }
    return (
      <p className="muted">
        {sources.length
          ? "No transport sources match the current filters."
          : "No transport sources in registry."}
      </p>
    );
  }

  return (
    <>
      <DataSourceControlsGrid
        sources={filteredSources}
        emptyMessage="No sources match the current filters."
        busyKey={busyCode}
        canManageSources={canManageSources}
        onRunNow={handleRunNow}
        onSaveSchedule={(source, payload) =>
          source.transportJobKey
            ? handleSaveNativeSchedule(source, payload)
            : overlaySchedule.handleSaveSchedule(source, payload)
        }
        onViewRuns={handleViewRuns}
        sectionLabel="Transport"
        deleteDomain="transport"
        onDeleted={() => onRefresh?.()}
        resolveRunNowTitle={(source) =>
          source.transportJobKey
            ? "Run the mapped transport scheduler job now"
            : "No automated runner is mapped to this source"
        }
      />
      <DataSourceScheduleModal
        source={overlaySchedule.scheduleSource}
        isOpen={Boolean(overlaySchedule.scheduleSource)}
        isSaving={overlaySchedule.scheduleSaving}
        onClose={() => overlaySchedule.setScheduleSource(null)}
        onSave={overlaySchedule.handleSaveSchedule}
      />
      <DataSourceScheduleModal
        source={nativeScheduleSource}
        isOpen={Boolean(nativeScheduleSource)}
        isSaving={nativeScheduleSaving}
        onClose={() => setNativeScheduleSource(null)}
        onSave={handleSaveNativeSchedule}
      />
    </>
  );
};

export default TransportSourceControlsTable;
